import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Trans, useTranslation } from '@edreamsodigeo/frontend-core';
import { Flex, Body, Box, RawMoney } from '@edreamsodigeo/prisma-design-system';
import { HeaderWithBadge } from '@/components/HeaderWithBadge';
import { WarningSign } from '@/components/WarningSign';
import { BenefitsCard } from '@/components/BenefitsCard';
import { PriceFreezeDate } from '@/components/PriceFreezeDateTime/PriceFreezeDateTime';
import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import { usePrimeBenefitsLink } from '@/providers/PrimeBenefitsLinkProvider';

const MoneyText = ({ amount, currency }) => (
  <RawMoney rounded={true} currency={currency}>
    {amount}
  </RawMoney>
);

MoneyText.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currency: PropTypes.string.isRequired,
};

export const AutoRenewal = ({
  subscriptionExpirationDate,
  depositPrice,
  currency,
}) => {
  const { track } = usePFTracking();
  const primeBenefitsLink = usePrimeBenefitsLink();

  useEffect(() => {
    track(ACTIONS.AUTO_RENEWAL.ONLOAD);
  }, [track]);

  const { t } = useTranslation();

  const translations = {
    badge: t('pricefreeze.autorenewal.intro.pill'),
    heading: t('pricefreeze.autorenewal.title'),
    terms: t('pricefreeze.autorenewal.terms', {
      primeKey: t('variables.prime.plan.primebasic'),
      primeTCKey: t('pricefreeze.prime.terms'),
    }),
    renderExpirationWarning: () => (
      <Trans
        i18nKey="pricefreeze.autorenewal.alert.txt"
        components={{
          priceKey: <MoneyText amount={depositPrice} currency={currency} />,
          dateKey: (
            <PriceFreezeDate
              format="ddMMMyyyy"
              date={subscriptionExpirationDate}
            />
          ),
        }}
      />
    ),
    benefitsTitle: t('pricefreeze.autorenewal.primeinfo.title'),
    renderBenefit1: () => (
      <Trans
        i18nKey="pricefreeze.autorenewal.primeinfo.first"
        components={{
          priceKey: <MoneyText amount={depositPrice} currency={currency} />,
        }}
      />
    ),
    renderBenefit2: t('pricefreeze.autorenewal.primeinfo.second', {
      priceKey: t('variables.primeSavingAverage'),
    }),
    renderBenefit3: () => (
      <Trans
        i18nKey="pricefreeze.autorenewal.primeinfo.third"
        components={{
          benefitLink: primeBenefitsLink,
        }}
      />
    ),
  };

  const benefits = [
    { id: 'benefit1', content: translations.renderBenefit1() },
    { id: 'benefit2', content: translations.renderBenefit2 },
    { id: 'benefit3', content: translations.renderBenefit3() },
  ];

  return (
    <Box backgroundColor="neutral.0">
      <Flex flexDirection="column" gap="base" aria-labelledby="renewal-page">
        <HeaderWithBadge
          badge={translations.badge}
          heading={translations.heading}
        />

        <WarningSign warningMessage={translations.renderExpirationWarning()} />

        <Flex gap="1x_small" flexDirection="column">
          <BenefitsCard
            title={translations.benefitsTitle}
            benefits={benefits}
          />
          <Body type="xSmall" weight="normal" color="neutral.70" inline>
            {translations.terms}
          </Body>
        </Flex>
      </Flex>
    </Box>
  );
};

AutoRenewal.propTypes = {
  subscriptionExpirationDate: PropTypes.string.isRequired,
  depositPrice: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
};
